import { LivePinService } from './livePin.service';
import { GpsTrackService } from './gpsTrack.service';
import { TrackingProviderDetector } from './trackingProviderDetector.service';
import { ParsedVehicleData } from './interfaces/LivePin.interface';
import { ParsedGpsTrackData } from './interfaces/GpsTrack.interface';

export class UnifiedTrackingService {
    private livePinService: LivePinService;
    private gpsTrackService: GpsTrackService;

    constructor() {
        this.livePinService = new LivePinService();
        this.gpsTrackService = new GpsTrackService();
    }

    /**
     * Fetch vehicle data from appropriate provider based on URL
     * @param url - Tracking URL
     * @returns Promise<ParsedVehicleData[]>
     */
    async fetchVehicleData(url: string): Promise<ParsedVehicleData[]> {
        const provider = TrackingProviderDetector.detectProvider(url);

        console.log(`🔍 Detected provider: ${provider} for URL: ${url}`);

        switch (provider) {
            case 'livepin':
                return await this.fetchLivePinData(url);

            case 'gpstrack':
                return await this.fetchGpsTrackData();

            default:
                console.error('❌ Unknown tracking provider for URL:', url);
                return [];
        }
    }

    /**
     * Fetch data from LivePin provider
     * @param url - LivePin URL
     * @returns Promise<ParsedVehicleData[]>
     */
    private async fetchLivePinData(url: string): Promise<ParsedVehicleData[]> {
        try {
            console.log('🚀 Fetching LivePin data...');
            const livePinData = await this.livePinService.fetchVehicleData(url);

            if (livePinData) {
                const parsedData = this.livePinService.parseVehicleData(livePinData);
                console.log('✅ LivePin data fetched successfully');
                return [parsedData];
            }

            console.warn('⚠️ No LivePin data received');
            return [];
        } catch (error) {
            console.error('❌ Error fetching LivePin data:', error);
            return [];
        }
    }

    /**
     * Fetch data from GPS Track provider
     * @returns Promise<ParsedVehicleData[]>
     */
    private async fetchGpsTrackData(): Promise<ParsedVehicleData[]> {
        try {
            console.log('🚀 Fetching GPS Track data...');
            const gpsTrackData = await this.gpsTrackService.fetchVehicleData();

            if (gpsTrackData && gpsTrackData.length > 0) {
                const parsedData = this.gpsTrackService.parseVehicleData(gpsTrackData);
                console.log('✅ GPS Track data fetched successfully');
                return parsedData;
            }

            console.warn('⚠️ No GPS Track data received');
            return [];
        } catch (error) {
            console.error('❌ Error fetching GPS Track data:', error);
            return [];
        }
    }

    /**
     * Check if URL is a valid tracking URL
     * @param url - URL to validate
     * @returns boolean
     */
    isValidTrackingUrl(url: string): boolean {
        return TrackingProviderDetector.isValidTrackingUrl(url);
    }

    /**
     * Get detailed validation information for a URL
     * @param url - URL to validate
     * @returns validation result
     */
    validateUrl(url: string) {
        return TrackingProviderDetector.validateUrl(url);
    }

    /**
     * Get provider information for a URL
     * @param url - URL to analyze
     * @returns provider info
     */
    getProviderInfo(url: string): {
        provider: 'livepin' | 'gpstrack' | 'unknown';
        isValid: boolean;
        message: string;
    } {
        const validation = TrackingProviderDetector.validateUrl(url);
        return {
            provider: validation.provider,
            isValid: validation.isValid,
            message: validation.message
        };
    }
}
